/** @typedef {{ city?: string|null, state?: string|null, country?: string|null, address?: string|null, display?: string|null, latitude?: number|null, longitude?: number|null, hasCity?: boolean }} ResolvedUserLocation */

export const formatCityDisplayName = (value) => {
  if (!value) return null;
  return String(value)
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

export const isSamePlaceName = (a, b) => {
  if (!a || !b) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
};

const isGhanaCountry = (country) => {
  const key = String(country || '')
    .trim()
    .toLowerCase();
  return key === 'ghana' || key === 'gh';
};

const isGreaterAccraRegion = (state) =>
  /greater\s*accra/i.test(String(state || '').trim());

/**
 * IP geolocation often labels Greater Accra metro as "Jema" — map to Accra for display/filters.
 */
export const normalizeDetectedCity = ({ city, state, country } = {}) => {
  const trimmed = city?.trim() || null;
  if (!trimmed) return null;

  if (!isGhanaCountry(country)) return trimmed;

  if (isGreaterAccraRegion(state)) {
    return 'Accra';
  }

  if (/^jema$/i.test(trimmed)) {
    return 'Accra';
  }

  return trimmed;
};

const countriesRoughlyMatch = (a, b) => {
  if (!a || !b) return false;
  const left = String(a).trim().toLowerCase();
  const right = String(b).trim().toLowerCase();
  if (left === right) return true;
  if ((left === 'ghana' || left === 'gh') && (right === 'ghana' || right === 'gh')) {
    return true;
  }
  return false;
};

/**
 * Human-readable label: "Accra, Ghana" — never country-only when city exists in profile/geo.
 */
export const formatLocationDisplay = ({ city, state, country } = {}) => {
  const cityLabel = formatCityDisplayName(city);
  const stateLabel = formatCityDisplayName(state);
  const countryLabel = country?.trim() || null;

  if (cityLabel && countryLabel && !isSamePlaceName(cityLabel, countryLabel)) {
    return `${cityLabel}, ${countryLabel}`;
  }
  if (cityLabel) return cityLabel;
  if (stateLabel && countryLabel && !isSamePlaceName(stateLabel, countryLabel)) {
    return `${stateLabel}, ${countryLabel}`;
  }
  return countryLabel || stateLabel || null;
};

/**
 * Merge browser geo + logged-in user profile into one consistent location context.
 */
export const resolveUserLocationContext = ({
  geoCity = null,
  geoState = null,
  geoCountry = null,
  address = null,
  latitude = null,
  longitude = null,
  user = null,
} = {}) => {
  const country = geoCountry || user?.country || null;
  const state = geoState || user?.state || null;

  const normalizedGeoCity = normalizeDetectedCity({
    city: geoCity,
    state,
    country,
  });

  const profileCity = user?.city?.trim() || null;
  const profileCountry = user?.country?.trim() || null;

  let city =
    profileCity && countriesRoughlyMatch(profileCountry, country)
      ? profileCity
      : normalizedGeoCity || profileCity || null;

  if (!city && address) {
    const parts = String(address)
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    const candidate = parts[0];
    if (candidate && !isSamePlaceName(candidate, country)) {
      city = candidate;
    }
  }

  if (city && isSamePlaceName(city, country)) {
    city =
      profileCity ||
      (normalizedGeoCity && !isSamePlaceName(normalizedGeoCity, country)
        ? normalizedGeoCity
        : null);
  }

  const display = formatLocationDisplay({ city, state, country });

  return {
    city: city || null,
    state: state || null,
    country: country || null,
    address: display,
    display,
    latitude: latitude ?? null,
    longitude: longitude ?? null,
    hasCity: Boolean(city),
  };
};

/** Slug for browse/chat filters (prefer city over country). */
export const getLocationFilterSlug = (resolved) => {
  if (!resolved) return null;
  if (resolved.city) {
    return String(resolved.city).trim().toLowerCase();
  }
  return null;
};
